import type { ToggleAiAnnotationsVisibilityProperties } from "@/analytics/ai-chat-events-properties";
import { EventType } from "@/analytics/analytics-events";
import { AI_GRADIENT_SMALL } from "@/components/common/ai-assets/ai-gradients";
import {
  selectBoundingBoxesOfElement,
  selectShowBoundingBoxes,
} from "@/features/ai-chat/ai-selectors";
import {
  setFocusedBoundingBox,
  setShowBoundingBoxes,
} from "@/features/ai-chat/ai-slice";
import { selectActiveElementId } from "@/store/selections-selectors";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  AiToggleIcon,
  FaroTooltip,
  grey,
  ToolButton,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { useCallback, type JSX } from "react";
import { ToolsTooltipText } from "../tools-toolbar";

/**
 * @returns A toolbar button that toggles the visibility of AI-detected
 * bounding boxes for the active element.
 *
 * On mount and whenever the active element changes, it fetches the VLM
 * analysis for that element and stores the image finding bounding boxes in the
 * store. Clicking the button toggles whether those bounding boxes are shown.
 */
export function AiBoundingBoxToggle(): JSX.Element {
  const showBoundingBoxes = useAppSelector(selectShowBoundingBoxes);
  const activeElementId = useAppSelector(selectActiveElementId);

  const boxes = useAppSelector(selectBoundingBoxesOfElement(activeElementId));
  const disableButton = !boxes.length;

  const dispatch = useAppDispatch();

  const toggleAiBboxVisibility = useCallback(() => {
    const enabling = !showBoundingBoxes;

    Analytics.track<ToggleAiAnnotationsVisibilityProperties>(
      EventType.toggleAiAnnotationsVisibility,
      { enabling },
    );

    // The toggle controls global visibility, so any single focused box is
    // cleared to keep the toggle state authoritative.
    dispatch(setFocusedBoundingBox(undefined));
    dispatch(setShowBoundingBoxes(enabling));
  }, [dispatch, showBoundingBoxes]);

  let tooltipText = `${showBoundingBoxes ? "Hide" : "Show"} AI annotations`;
  if (disableButton) {
    tooltipText = "No AI annotations available for this scene.";
  }

  return (
    <FaroTooltip
      placement="left"
      title={<ToolsTooltipText text={tooltipText} />}
    >
      {/* <Box component="span"> */}
      <ToolButton
        disabled={disableButton}
        aria-label="Toggle AI annotations"
        onClick={toggleAiBboxVisibility}
        selected={showBoundingBoxes}
        sx={showBoundingBoxes ? AI_GRADIENT_SMALL : undefined}
      >
        <AiToggleIcon sx={showBoundingBoxes ? { color: grey[0] } : undefined} />
      </ToolButton>
      {/* </Box> */}
    </FaroTooltip>
  );
}
